import type { CompetitionLogoDto, FormFieldDto, SubmissionStatus } from '@napaicp/schemas'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { competitionKeys } from '@/features/competitions/query-keys'
import { apiRequest, apiRequestWithMeta } from '@/lib/api-client'

export interface PublicCompetitionSummary {
  name: string
  slug: string
  officialWebsiteUrl: string | null
  logo: CompetitionLogoDto | null
  shortDescription: string | null
  description: string | null
  /** UX-078: surfaced in the hero so every date on the page has explicit timezone context. */
  timezone: string
  status: string
  /** PR-125: derived server-side from `teamMaxMembers === 1`, not a stored field. */
  participationType: 'individual' | 'team'
  /** PR-160: existing `competitions` team-size/advisor rules, exposed for the "參賽重點" highlights block. */
  teamMinMembers: number
  teamMaxMembers: number
  allowTeacher: boolean
  requireTeacher: boolean
  registrationStartAt: string | null
  registrationEndAt: string | null
  submissionStartAt: string | null
  submissionEndAt: string | null
  resultPublishAt: string | null
  registrationInstructions: string | null
  /** Public Competition Page v1 structured fields Spec Delta (2026-08-26): admin free-text, `null` hides the section. */
  eligibilityText: string | null
  submissionRequirementsText: string | null
  judgingCriteriaText: string | null
  prizesText: string | null
  organizerContactText: string | null
  faq: { question: string; answer: string }[]
  categories: { name: string; description: string | null }[]
  /** PR-130: platform Discovery Category names this competition opted into — distinct from `categories` above. */
  discoveryCategories: { name: string; slug: string }[]
  announcements: { id: string; title: string; body: string; publishedAt: string }[]
}

/** PR-125 — every field optional; an empty object behaves exactly like the old no-arg call. */
export interface PublicCompetitionListFilters {
  q?: string
  status?: 'published' | 'registration_open' | 'submission_open' | 'judging' | 'completed'
  participationType?: 'individual' | 'team'
  closingSoon?: boolean
  /** PR-130: filters by Discovery Category slug. */
  category?: string
  sort?: 'newest' | 'deadline'
  limit?: number
  offset?: number
}

export interface PublicCompetitionListPage {
  competitions: PublicCompetitionSummary[]
  total: number
  limit: number
  offset: number
}

/**
 * The competition's published `registration_form`, if it has one — `null`
 * otherwise. Readable by anyone: unlike the authenticated
 * `/api/competitions/:id/forms` list, this has to work for a visitor who
 * does not hold a role in the competition yet, since filling it out is how
 * most of them get one.
 */
export interface PublicRegistrationForm {
  formId: string
  fields: FormFieldDto[]
}

/**
 * One round's own schedule — a competition may have up to two (preliminary
 * and final), each with independent dates. `draft` rounds are never
 * included: an admin still configuring a round's dates has not announced
 * them yet.
 */
export interface PublicRoundSummary {
  key: string
  name: string
  status: string
  registrationStartAt: string | null
  registrationEndAt: string | null
  submissionStartAt: string | null
  submissionEndAt: string | null
  resultPublishAt: string | null
}

/**
 * PR-133/API-069: a signed-in visitor's own participation state for this
 * competition — `null` for an anonymous visitor and for a signed-in visitor
 * who holds no role here (the two are indistinguishable by design).
 */
export interface PublicCompetitionMyStatus {
  competitionId: string
  role: string
  registrationFormCompleted: boolean | null
  hasTeam: boolean | null
  submissionStatus: SubmissionStatus | null
  /** PR-258/BR-191: see `StudentJourneyInput.wasSubmittedBefore`. */
  wasSubmittedBefore: boolean
  resultPublished: boolean
  /** PR-264 (Wave SJ-5 API delta): see `StudentJourneyInput.isFinalRound`. */
  isFinalRound: boolean
}

/** Everything an anonymous visitor can see about one competition, plus `myStatus` when signed in. */
export type PublicCompetition = PublicCompetitionSummary & {
  registrationForm: PublicRegistrationForm | null
  rounds: PublicRoundSummary[]
  /**
   * PR-136/BR-118 root-cause-B: which of `rounds` (by `key`) the server used
   * to derive `status` — `null` when no round is currently "the" round (no
   * rounds at all, or none currently `open`/`judging`), in which case
   * `status` was derived from the competition-level envelope fields above.
   * Feed this and `rounds` into `resolveCurrentScheduleDates` rather than
   * reading the envelope fields directly, so the Hero/countdown always agree
   * with the timeline's "current" row.
   */
  currentRoundKey: string | null
  myStatus: PublicCompetitionMyStatus | null
}

export interface PublicDiscoveryCategory {
  id: string
  name: string
  slug: string
}

/**
 * PR-130/131 — the active Discovery Category taxonomy. Used both for the
 * public filter dropdown and the (authenticated) per-competition category
 * picker on `CompetitionOverviewPage` — see the API route's own comment for
 * why `id` is included despite the "small public DTO" convention.
 */
export function usePublicDiscoveryCategories(): UseQueryResult<PublicDiscoveryCategory[]> {
  return useQuery({
    queryKey: ['public-discovery-categories'],
    queryFn: () => apiRequest<PublicDiscoveryCategory[]>('/api/public/discovery-categories'),
  })
}

/**
 * Grants the signed-in caller `student` in the competition identified by
 * `slug`, if its schedule currently reads as open for registration. Fails
 * with a validation error (surfaced via `ApiError`) outside that window or
 * if the caller already holds a role there.
 */
export function useSelfRegister(slug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    // `answers` is only ever sent (and only ever required by the server)
    // when the competition has a published `registration_form` — omitting it
    // entirely keeps the original one-click flow for every competition that
    // has no such form.
    mutationFn: (answers?: Record<string, unknown>) =>
      apiRequest<{ competitionId: string; role: string }>(
        `/api/public/competitions/${encodeURIComponent(slug)}/self-register`,
        answers === undefined ? { method: 'POST' } : { method: 'POST', body: { answers } },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.all })
    },
  })
}

/**
 * Every competition currently visible to an anonymous visitor, optionally
 * filtered/sorted/paginated (PR-125). Called with no arguments, this is
 * unchanged from before: the server's `limit` defaults to 50, well above
 * this platform's current competition count, so existing callers that
 * render a small slice of "everything" keep working exactly as before.
 */
export function usePublicCompetitions(
  filters: PublicCompetitionListFilters = {},
): UseQueryResult<PublicCompetitionListPage> {
  const search = new URLSearchParams()
  if (filters.q !== undefined && filters.q.length > 0) search.set('q', filters.q)
  if (filters.status !== undefined) search.set('status', filters.status)
  if (filters.participationType !== undefined)
    search.set('participationType', filters.participationType)
  if (filters.closingSoon !== undefined) search.set('closingSoon', String(filters.closingSoon))
  if (filters.category !== undefined && filters.category.length > 0)
    search.set('category', filters.category)
  if (filters.sort !== undefined) search.set('sort', filters.sort)
  if (filters.limit !== undefined) search.set('limit', String(filters.limit))
  if (filters.offset !== undefined) search.set('offset', String(filters.offset))
  const queryString = search.toString()

  return useQuery({
    queryKey: ['public-competitions', filters],
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<{ competitions: PublicCompetitionSummary[] }>(
        `/api/public/competitions${queryString.length > 0 ? `?${queryString}` : ''}`,
      )
      return {
        competitions: data.competitions,
        total: Number(meta.total ?? data.competitions.length),
        limit: Number(meta.limit ?? data.competitions.length),
        offset: Number(meta.offset ?? 0),
      }
    },
  })
}
